import z from 'zod'

/** Coerce region codes from JSON search params (number or quoted string). */
const regionCodeSchema = z.preprocess(value => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  if (typeof value === 'number') {
    return String(value)
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (
      trimmed.length >= 2 &&
      trimmed.startsWith('"') &&
      trimmed.endsWith('"')
    ) {
      try {
        return String(JSON.parse(trimmed))
      } catch {
        return trimmed.slice(1, -1)
      }
    }
    return trimmed
  }

  return value
}, z.string().optional())

export const MEMBER_POKTAN_NIK_LENGTH = 16

export const memberPoktanSearchByOptions = ['nik', 'poktan'] as const
export type MemberPoktanSearchBy = (typeof memberPoktanSearchByOptions)[number]

export const memberPoktanSearchByLabels: Record<MemberPoktanSearchBy, string> =
  {
    nik: 'Cari berdasarkan NIK',
    poktan: 'Cari berdasarkan Poktan'
  }

export function sanitizeMemberPoktanNik(value: string) {
  return value.replace(/\D/g, '').slice(0, MEMBER_POKTAN_NIK_LENGTH)
}

export function isCompleteMemberPoktanNik(value?: string) {
  return (value ?? '').length === MEMBER_POKTAN_NIK_LENGTH
}

const nikSearchSchema = z.preprocess(value => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  const digits = sanitizeMemberPoktanNik(String(value))
  return isCompleteMemberPoktanNik(digits) ? digits : undefined
}, z.string().length(MEMBER_POKTAN_NIK_LENGTH).optional())

export const searchParamsSchema = z.object({
  search_by: z.enum(memberPoktanSearchByOptions).optional(),
  poktan_id: z.string().optional(),
  search: nikSearchSchema,
  province_code: regionCodeSchema,
  city_code: regionCodeSchema,
  district_code: regionCodeSchema,
  village_code: regionCodeSchema,
  per_page: z.number().min(10).max(100).default(10),
  page: z.number().min(1).default(1)
})

export type SearchParams = z.infer<typeof searchParamsSchema>

/** Resolve active search mode from URL, preferring explicit search_by. */
export function resolveMemberPoktanSearchBy(
  search: Pick<SearchParams, 'search_by' | 'search' | 'poktan_id'>
): MemberPoktanSearchBy {
  if (search.search_by) return search.search_by
  if (search.search) return 'nik'
  if (search.poktan_id) return 'poktan'
  return 'nik'
}
